import { bytesToGb, bytesToMb } from 'blaze-components/MediaUploadModal/utils'
import { isImage, isVideo } from 'components/ImagePickerModal/utils'
import { MediaObject } from 'types'
import { pluralize } from 'utils'

export interface ContentRules {
  maxCharacters?: number
  maxHashtags?: number
  maxMentions?: number
}

export interface FileRules {
  minImages?: number
  maxImages?: number
}

const validateTextLength = (content: string, maxCharacters: number, contentRequired: boolean = true): string[] => {
  const errors: string[] = []
  if (content.length > maxCharacters) {
    errors.push(`Content exceeds ${maxCharacters} characters (${content.length})`)
  }
  if (content.length === 0 && contentRequired) {
    errors.push('Content is required')
  }
  return errors
}

const validateHashtags = (content: string, maxHashtags: number): string[] => {
  const errors: string[] = []
  const hashtags = content.match(/#[a-zA-Z0-9]+/g) || []
  if (hashtags.length > maxHashtags) {
    errors.push(`Content contains more than the limit of ${maxHashtags} hashtags`)
  }
  return errors
}

const validateMentions = (content: string, maxMentions: number): string[] => {
  const errors: string[] = []
  const mentions = content.match(/@[a-zA-Z0-9]+/g) || []
  if (mentions.length > maxMentions) {
    errors.push(`Content contains more than the limit of ${maxMentions} mentions`)
  }
  return errors
}

const validateFileCount = (files: MediaObject[], minImages: number, maxImages: number): string[] => {
  const errors: string[] = []
  if (files.length < minImages) {
    errors.push(`At least ${minImages} media file is required`)
  }
  if (files.length > maxImages) {
    errors.push(
      `No more than ${maxImages} media ${pluralize('file', maxImages)} ${maxImages === 1 ? 'is' : 'are'} allowed`,
    )
  }
  return errors
}

const validateVideosCount = (files: MediaObject[], minVideos: number, maxVideos: number): string[] => {
  const videos = files.filter((file) => isVideo({ type: file.mime_type, url: file.source_url }))
  const images = files.filter((file) => isImage({ type: file.mime_type, url: file.source_url }))
  const errors: string[] = []

  if (videos.length > maxVideos) {
    errors.push(
      `Only ${maxVideos} ${pluralize('video', maxVideos)} per post ${
        maxVideos === 1 ? 'is' : 'are'
      } supported. Please remove a video`,
    )
  }
  if (videos.length === 1 && images.length > 0) {
    errors.push(`Videos and images are not supported on the same post. Please remove one type`)
  }
  if (videos.length < minVideos) {
    errors.push(`At least ${minVideos} ${pluralize('video', minVideos)} ${minVideos === 1 ? 'is' : 'are'} required`)
  }

  return errors
}

const validateVideosSize = (files: MediaObject[], maxSize: number, unit: string): string[] => {
  const videos = files.filter((file) => isVideo({ type: file.mime_type, url: file.source_url }))
  const maxSizeInUnit = unit === 'MB' ? bytesToMb(maxSize) : bytesToGb(maxSize)
  const errors: string[] = []

  if (videos.some((video) => video.size && video.size > maxSize)) {
    errors.push(`Video uploads must be ${maxSizeInUnit}${unit} or smaller. Please compress your video and reupload it`)
  }

  return errors
}

const validateImagesSize = (files: MediaObject[], maxSize: number, unit: string): string[] => {
  const videos = files.filter((file) => isImage({ type: file.mime_type, url: file.source_url }))
  const maxSizeInUnit = unit === 'MB' ? bytesToMb(maxSize) : bytesToGb(maxSize)
  const errors: string[] = []

  if (videos.some((video) => video.size && video.size > maxSize)) {
    errors.push(`Images must be ${maxSizeInUnit}${unit} or smaller. Please compress your image and reupload it`)
  }

  return errors
}

const validateVideosDuration = (
  files: MediaObject[],
  minDuration: number,
  maxDuration: number,
  unit: string = 'minutes',
): string[] => {
  const videos = files.filter((file) => isVideo({ type: file.mime_type, url: file.source_url }))
  const errors: string[] = []

  if (videos.some((video) => video.duration && (video.duration > maxDuration || video.duration < minDuration))) {
    if (unit === 'seconds') {
      errors.push(`Videos need to be between ${minDuration} and ${maxDuration} seconds long to upload successfully`)
    } else {
      errors.push(
        `Videos need to be between ${minDuration} ${pluralize('second', minDuration)} and ${
          maxDuration / 60
        } minutes long to upload successfully`,
      )
    }
  }

  return errors
}

const validateVideosAspectRatio = (
  files: MediaObject[],
  minAspectRatio: number,
  maxAspectRatio: number,
  minAspectRatioLabel: string,
  maxAspectRatioLabel: string,
): string[] => {
  const videos = files.filter((file) => isVideo({ type: file.mime_type, url: file.source_url }))
  const errors: string[] = []

  if (
    videos.some(
      (video) => video.aspect_ratio && (video.aspect_ratio < minAspectRatio || video.aspect_ratio > maxAspectRatio),
    )
  ) {
    errors.push(
      `Videos need to have an aspect ratio greater than ${minAspectRatioLabel} and lower than ${maxAspectRatioLabel} to upload successfully`,
    )
  }

  return errors
}

const validateVideosFormat = (files: MediaObject[], formats: string[], provider: string): string[] => {
  const videos = files.filter((file) => isVideo({ type: file.mime_type, url: file.source_url }))
  const errors: string[] = []

  if (videos.some((video) => video.mime_type && !formats.includes(video.mime_type))) {
    const cleanedFormats = formats.map((format) => format.replaceAll('video/', '.').replace('quicktime', 'mov'))

    let formatsToString: string
    if (cleanedFormats.length <= 2) {
      formatsToString = cleanedFormats.join(' and ')
    } else {
      const lastFormat = cleanedFormats.pop()
      formatsToString = `${cleanedFormats.join(', ')}, and ${lastFormat}`
    }

    errors.push(`${provider} supports videos in ${formatsToString}. Please convert your video and reupload it`)
  }

  return errors
}

const validators = {
  validateTextLength,
  validateHashtags,
  validateMentions,
  validateFileCount,
  validateVideosCount,
  validateVideosSize,
  validateImagesSize,
  validateVideosDuration,
  validateVideosAspectRatio,
  validateVideosFormat,
}

export default validators
